import { gql } from '@apollo/client';
import {
  Box,
  Button,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Radio,
  RadioGroup,
  Stack,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { t } from 'i18next';
import { useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useAuth } from '~auth/useAuth';
import { ChevronDownIcon } from '~components/ui/icons';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { ServiceAdminRole } from '~graphql/__generated__/types';
import { fromError } from '~utils/errors';
import { ConfigureAdminRolesModal_UserFragment } from './__generated__/ConfigureAdminRolesModal.graphql';
import { getAdminRolesErrorMap } from './adminRolesErrorMap';
import { getServiceAdminRoleCategories } from './serviceAdminRoleCategories';
import { useUpdateAdminRoles } from './UpdateAdminRoles';

const NONE = 'NONE';

interface Props {
  user: ConfigureAdminRolesModal_UserFragment | null;
  mode: 'create' | 'edit';
  onCancel: () => void;
  onSuccess: () => void;
}

function buildInitialSelection(
  user: ConfigureAdminRolesModal_UserFragment | null,
): Record<string, string> {
  const categories = getServiceAdminRoleCategories();
  const selection: Record<string, string> = {};

  categories.forEach((category) => {
    const currentRole = category.options.find((option) =>
      user?.serviceAdminRoles.includes(option.role),
    );
    selection[category.key] = currentRole?.role ?? NONE;
  });

  return selection;
}

export function ConfigureAdminRolesModal({ user, mode, onCancel, onSuccess }: Props) {
  const toast = useToast();
  const { organization } = useAuth();
  const [updateAdminRoles, { loading }] = useUpdateAdminRoles();
  const categories = useMemo(() => getServiceAdminRoleCategories(), []);
  const [selection, setSelection] = useState<Record<string, string>>(() =>
    buildInitialSelection(user),
  );

  if (!user) {
    return null;
  }

  const selectedCount = Object.values(selection).filter((role) => role !== NONE).length;
  const accessSummary =
    selectedCount > 0 ? t('accessSelectedCount', { count: selectedCount }) : t('none');

  const handleSubmit = async () => {
    const serviceAdminRoles = Object.values(selection).filter(
      (role): role is ServiceAdminRole => role !== NONE,
    );

    try {
      await updateAdminRoles(user, serviceAdminRoles);

      toast({
        status: 'success',
        title: (
          <Trans
            i18nKey="adminUpgradeSuccess"
            values={{
              name: `${user.givenName} ${user.familyName}`,
              organizationName: organization?.name ?? '',
            }}
            components={{ strong: <strong /> }}
          />
        ),
      });

      onSuccess();
    } catch (err) {
      toast({
        status: 'error',
        title: t('adminUpgradeError'),
        description: fromError(err, 'UpdateAdminRoles', getAdminRolesErrorMap()),
      });
    }
  };

  return (
    <Modal isOpen={Boolean(user)} onClose={onCancel} size="lg">
      <ModalOverlay />
      <ModalContent minHeight="600px">
        <ModalHeader>{t(mode === 'create' ? 'addAdmin' : 'editAdminRoles')}</ModalHeader>
        <ModalCloseButton onClick={onCancel} />
        <ModalBody>
          <VStack align="stretch" spacing="4">
            <FormLabel mb="0">{t('fullName')}</FormLabel>
            <Input value={`${user.givenName} ${user.familyName}`} isReadOnly isDisabled />

            <FormLabel mb="0">{t('email')}</FormLabel>
            <Input value={user.email} isReadOnly isDisabled />

            <FormLabel mb="0">{t('access')}</FormLabel>
            <Popover placement="bottom-start" closeOnBlur isLazy matchWidth>
              <PopoverTrigger>
                <Button
                  variant="outline"
                  colorScheme="gray"
                  justifyContent="space-between"
                  fontWeight="normal"
                  width="100%"
                  rightIcon={<ChevronDownIcon />}
                >
                  <Box as="span" color={selectedCount > 0 ? 'inherit' : 'gray.500'}>
                    {accessSummary}
                  </Box>
                </Button>
              </PopoverTrigger>

              <PopoverContent width="100%">
                <PopoverBody maxHeight="160px" overflowY="auto">
                  <VStack align="stretch" spacing="3">
                    {categories.map((category) => (
                      <VStack align="stretch" spacing="1" key={category.key}>
                        <FormLabel mb="0" fontWeight="semibold" fontSize="sm" paddingLeft="3">
                          {category.label}
                        </FormLabel>
                        <RadioGroup
                          value={selection[category.key]}
                          onChange={(value) =>
                            setSelection((prev) => ({ ...prev, [category.key]: value }))
                          }
                        >
                          <Stack direction="column" spacing="1" paddingLeft="5">
                            <Radio value={NONE} fontSize="sm">
                              {t('none')}
                            </Radio>
                            {category.options.map((option) => (
                              <Radio key={option.role} value={option.role} fontSize="sm">
                                {option.label}
                              </Radio>
                            ))}
                          </Stack>
                        </RadioGroup>
                      </VStack>
                    ))}
                  </VStack>
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="ghost"
            colorScheme="gray"
            marginLeft="auto"
            onClick={onCancel}
            isDisabled={loading}
          >
            {t('cancel')}
          </Button>
          <Button
            variant="solid"
            colorScheme="blue"
            marginLeft="3"
            onClick={handleSubmit}
            isLoading={loading}
          >
            {t(mode === 'create' ? 'upgradeToAdminRole' : 'save')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

ConfigureAdminRolesModal.graphql = {
  fragments: {
    ConfigureAdminRolesModal_user: gql`
      fragment ConfigureAdminRolesModal_user on User {
        id
        givenName
        familyName
        email
        roles
        serviceAdminRoles
        customerIds
      }
    `,
  },
};
